import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';

export type Difficulty = 'easy' | 'medium' | 'hard';

export interface Cell {
  hasMine: boolean;
  revealed: boolean;
  flagged: boolean;
  adjacentMines: number;
}

type TopScore = { name: string, score: number, created_at: string };

@Component({
  selector: 'app-minesweeper',
  templateUrl: './minesweeper.component.html',
  styleUrls: ['./minesweeper.component.scss']
})
export class MinesweeperComponent implements OnInit {
    board: Cell[][] = [];
    gameOver = false;
    gameWon = false;

    difficulty: Difficulty = 'easy';
    rows = 9;
    cols = 9;
    mines = 10;

    timer = 0;
    timerInterval: any;
    timerStarted = false;

    topScores: TopScore[] = [];

    constructor(
        private httpClient: HttpClient,
        public authService: AuthService
    ) { };

    ngOnInit() {
        this.resetGame();
    }

    setDifficulty(level: Difficulty) {
        this.difficulty = level;
        if (level === 'easy') {
              this.rows = 9; this.cols = 9; this.mines = 10;
        } else if (level === 'medium') {
              this.rows = 16; this.cols = 16; this.mines = 40;
        } else if (level === 'hard') {
           this.rows = 16; this.cols = 30; this.mines = 99;
        }
        this.resetGame();
    }
    
    startTimer() {
        if (!this.timerStarted) {
            this.timerStarted = true;
            this.timerInterval = setInterval(() => this.timer++, 1000);
        }
    }
    
    stopTimer() {
        clearInterval(this.timerInterval);
        this.timerStarted = false;
    }

    resetGame() {
        this.stopTimer();
        this.timer = 0;
        this.board = [];
        this.gameOver = false;
        this.gameWon = false;
        this.timerStarted = false;

        // Init empty board
        this.board = Array.from({ length: this.rows }, () =>
            Array.from({ length: this.cols }, () => ({
                hasMine: false,
                revealed: false,
                flagged: false,
                adjacentMines: 0
            }))
        );

        // Place mines
        let placed = 0;
        while (placed < this.mines) {
            const r = Math.floor(Math.random() * this.rows);
            const c = Math.floor(Math.random() * this.cols);
            if (!this.board[r][c].hasMine) {
                this.board[r][c].hasMine = true;
                placed++;
            }
        }

        // Calculate adjacent mines
        for (let r = 0; r < this.rows; r++) {
            for (let c = 0; c < this.cols; c++) {
                this.board[r][c].adjacentMines = this.countAdjacentMines(r, c);
            }
        }

        this.timer = 0;
        this.timerStarted = false;
        clearInterval(this.timerInterval);
    }

    countAdjacentMines(row: number, col: number): number {
        let count = 0;
        for (let i = row - 1; i <= row + 1; i++) {
            for (let j = col - 1; j <= col + 1; j++) {
                if (i >= 0 && i < this.rows && j >= 0 && j < this.cols && !(i === row && j === col) && this.board[i][j].hasMine) {
                    count++;
                }
            }
        }
        return count;
    }

    revealCell(row: number, col: number, checkWin = true) {
        if (!this.timerStarted && !this.gameOver) this.startTimer();
        const cell = this.board[row][col];
        if (cell.revealed || cell.flagged || this.gameOver) return;
    
        cell.revealed = true;
    
        if (cell.hasMine) {
            this.gameOver = true;
            setTimeout(() => {
                alert('💥 Boom! Game Over.');
                this.stopTimer();
            }, 100); // Delay slightly to allow DOM/render updates
            return;
        }
    
        if (cell.adjacentMines === 0) {
          // Reveal neighbors
            for (let i = row - 1; i <= row + 1; i++) {
                for (let j = col - 1; j <= col + 1; j++) {
                    if (i >= 0 && i < this.rows && j >= 0 && j < this.cols) {
                        this.revealCell(i, j, false);
                    }
                }
            }
        }
    
        if (checkWin) {
            this.checkWin();
        }
    }

    toggleFlag(event: MouseEvent, row: number, col: number) {
        event.preventDefault(); // Prevent context menu
        const cell = this.board[row][col];
        if (!cell.revealed && !this.gameOver) {
            cell.flagged = !cell.flagged;
        }
    }

    checkWin() {
        for (let row of this.board) {
          for (let cell of row) {
            if (!cell.hasMine && !cell.revealed) return;
          }
        }
        this.gameWon = true;
        this.gameOver = true;
        setTimeout(() => {
            alert('🎉 You Win!');
            this.stopTimer();
            this.saveScore();
            this.getTopScores();
        }, 100); // Delay slightly to allow DOM/render updates
    }

    saveScore() {
        if (this.authService.loggedIn()) {   
          const token = this.authService.getToken(); 
          this.httpClient.post(
            `https://ng.renner.se/games.api.php?action=save_score`,
            { 
              game_id: 3,
              score: this.timer,
              difficulty: this.difficulty 
            },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          ).subscribe({
            next: (response) => {
              console.log('Score saved successfully:', response);
            },
            error: (err) => {
              console.error('Failed to save score:', err);
            }
          });
        }
    }

    getTopScores() {
        let token = this.authService.getToken();
        return this.httpClient.get<TopScore[]>(
            `https://ng.renner.se/games.api.php?action=get_top_scores`,
            {
                params: {
                    game_id: 3,
                    difficulty: this.difficulty,
                    order: 'ASC',
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        ).subscribe({
            next: (scores) => {
              console.log('Scores: ', scores);
              this.topScores = scores;
            },
            error: (err) => {
              console.error('Failed to get top score:', err);
            }

        });
    }
    
}
